import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { graphQlClient } from '@config/graphqlClient';
import ActivitiesGraphQL from '@graphql/activity.queries';
import { Activity } from '@models/activity.model';
import { useTranslation } from '@hooks/useTranslation';
import { getErrorMessage } from '@utils/errorMessage';

export type PagedActivities = {
  total: number;
  items: Activity[];
};

export const ACTIVITIES_BY_TASK_QUERY_KEY = (
  projectId?: string,
  taskId?: string,
  page?: number,
  limit?: number,
) => ['activities', 'byTask', projectId, taskId, { page, limit }];

export type UseActivityByTaskArgs = {
  projectId?: string;
  taskId?: string;
  page?: number;
  limit?: number;
};

export const useActivityByTask = (
  { projectId, taskId, page, limit }: UseActivityByTaskArgs,
  options?: Partial<UseQueryOptions<PagedActivities, Error>>,
) => {
  const localeCommon = useTranslation('common');

  return useQuery<PagedActivities, Error>({
    queryKey: ACTIVITIES_BY_TASK_QUERY_KEY(projectId, taskId, page, limit),
    enabled: Boolean(projectId && taskId),
    queryFn: async () => {
      try {
        const data: any = await graphQlClient.request(
          ActivitiesGraphQL.queries.getActivitiesByTaskId,
          {
            projectId,
            taskId,
            page,
            limit,
          },
        );
        return data?.getActivitiesByTaskId ?? { total: 0, items: [] };
      } catch (error: any) {
        toast.error(getErrorMessage(error, localeCommon['requestError']));
        throw error;
      }
    },
    ...options,
  });
};

export default useActivityByTask;
